import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CtaSection from '../components/CtaSection';
import ServicesOverviewGrid from '../components/ServicesOverviewGrid';
import { getOptimizedImageUrl } from '../lib/cms';

export default function ServicesPage() {
  const serviceCategories = [
    {
      id: 'content-redigering',
      slug: 'content-redigering',
      icon: 'fa-light fa-video',
      tag: 'Klipning & Content',
      title: 'Content Opsætning & Redigering',
      subtitle: 'I optager selv hverdagens øjeblikke – vi overtager det tidskrævende arbejde med professionel klipning, tekstning og formatering.',
      image: 'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png',
      features: [
        'Professionel redigering i 9:16 vertikalt format til Reels, TikTok og Shorts',
        'Dynamiske undertekster og optimerede hooks, der fastholder seerne',
        'Valg af royalty-free baggrundsmusik og trending lyde',
        'Farvejustering, renovering af lyd og indsættelse af brandidentitet',
        'Klar til publicering – leveret hurtigt og ubesværet online',
        'Fleksibel volumen tilpasset jeres aktuelle kampagner og behov',
      ],
      description:
        'I behøver ikke optageudstyr til mange tusinde kroner for at have en levende profil. I optager blot med jeres smartphone og sender råfilerne online til os. Vi forvandler jeres optagelser til professionelt, fængende videoindhold, der skaber maksimal synlighed, engagerer jeres målgruppe og driver salg i jeres forretning.',
    },
    {
      id: 'online-vejledning',
      slug: 'online-vejledning',
      icon: 'fa-light fa-camera-viewfinder',
      tag: 'Online Vejledning',
      title: 'Online Vejledning til Video & Billeder',
      subtitle: 'Sparring og vejledning til jeres egne optagelser – og fleksibilitet til at komme ud til jer ved nærmere aftale.',
      image: 'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/service-some-guidance.png',
      features: [
        'Konkret sparring på kameravinkler, naturligt lys og rolig håndtering',
        'Tips til at fange hotelværelser, Airbnb-stemning, retter eller butiksdetaljer',
        'Idéoplæg og strukturerede skydelister (hvad skal I filme i dag?)',
        'Lydoptimering og undgåelse af støj ved smartphone-optagelser',
        'Løbende online feedback på jeres testoptagelser og vinkler',
        'Fleksibel mulighed for at vi kommer ud og hjælper on-site ved nærmere aftale',
      ],
      description:
        'Mange virksomheder vil gerne være aktive på sociale medier, men ved ikke hvad eller hvordan de skal filme. Vi giver jer online vejledning, inspiration og konkrete retningslinjer, så I selv hurtigt og enkelt kan optage det nødvendige materiale uden at spilde tid.',
    },
    {
      id: 'meta-postkalender',
      slug: 'meta-postkalender',
      icon: 'fa-light fa-calendar-days',
      tag: 'Meta & Planlægning',
      title: 'Meta SoMe Postkalender & Planlægning',
      subtitle: 'Oprettelse og løbende vedligeholdelse af jeres content- og postkalender i Meta, så I udkommer fast uden stress.',
      image: 'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/service-meta-calendar.png',
      features: [
        'Oprettelse og løbende opdatering af postkalender i Meta Business Suite',
        'Planlægning af opslag, Reels og Stories på de mest optimale tidspunkter',
        'Udarbejdelse af engagerende opslagstekster (captions) og relevante tags',
        'Visuelt månedsoverblik til jeres forhåndsgodkendelse',
        'Sikrer kontinuitet og en rød tråd på både Instagram og Facebook',
        'I sparer tid i hverdagen og bevarer det fulde overblik',
      ],
      description:
        'En velstruktureret postkalender er nøglen til succes på Meta. Vi opretter og vedligeholder kalenderen for jer, forbereder tekster og planlægger udgivelserne, så I aldrig mere skal spekulere på, hvad der skal postes i morgen.',
    },
    {
      id: 'profil-opsaetning',
      slug: 'profil-opsaetning',
      icon: 'fa-light fa-sliders',
      tag: 'Profil & Identitet',
      title: 'Opsætning af Sociale Medier',
      subtitle: 'Professionel opsætning og optimering af jeres SoMe profiler på tværs af de platforme, I ønsker at være synlige på.',
      image: 'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/agency-founders-meeting.png',
      features: [
        'Gennemgang og optimering af eksisterende profiler eller komplet nyoprettelse',
        'Skarp bio-tekst med klart værdiforslag og handlingsopfordring (CTA)',
        'Design af visuelle story highlights (højdepunktsikoner) og profilgrafik',
        'Opsætning af link-in-bio struktur til hjemmeside, booking eller katalog',
        'Tilknytning til Meta Business Manager og korrekt kontokonfiguration',
        'Gør profilen klar til at konvertere besøgende til betalende kunder',
      ],
      description:
        'Jeres profil på sociale medier er forretningens digitale facade. Vi kan stå for at sætte jeres profiler op fra bunden eller finpudse de eksisterende, så jeres virksomhed fremstår knivskarp, indbydende og professionel overfor nye følgere.',
    },
  ];

  const whyChooseUs = [
    {
      icon: 'fa-light fa-comments-question',
      title: 'Tæt sparring & rådgivning',
      desc: 'Vi giver jer konkret sparring og idéer til indholdet, så I altid ved, hvad I skal optage for at fange målgruppen.',
    },
    {
      icon: 'fa-light fa-handshake-slash',
      title: '0 binding',
      desc: 'Vi tror på frie, fleksible samarbejder. I er aldrig bundet af lange kontrakter, men kan justere efter behov.',
    },
    {
      icon: 'fa-light fa-gift',
      title: 'Betal 2 mdr – få 1 mdr gratis',
      desc: 'Benyt vores fordelagtige tilbud: betal for 2 måneders service og få den 3. måned helt gratis uden binding.',
    },
    {
      icon: 'fa-light fa-sliders',
      title: 'Ingen faste pakker',
      desc: 'Alle aftaler laves individuelt gennem kontakt, fordi ethvert firma og forretning har brug for noget forskelligt.',
    },
  ];

  return (
    <>
      <SeoHead
        title="Ydelser | SoMe Sparring, Redigering & Synlighed"
        description="Professionel sparring på optagelser, content-redigering, opsætning og styring af Meta-postkalender. Skab synlighed med 0 binding."
      />

      {/* Hero Header */}
      <section className="pt-40 pb-16 md:pt-48 lg:pt-52 md:pb-24 bg-white border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FAF8F5] border border-[#E5DFD6] text-xs font-semibold uppercase tracking-widest text-[#A8885B]">
              Sparring · Redigering · Synlighed
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#161514] leading-tight">
              Gennemført SoMe-service –{' '}
              <span className="text-[#876F50]">uden binding og faste pakker</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#5C5852] leading-relaxed">
              Vi hjælper virksomheder med professionel content-redigering, sparring på egne optagelser, styring af Meta-postkalender samt opsætning af sociale medier – skræddersyet til at skabe synlighed, engagement og øget salg.
            </p>
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#161514] text-[#FAF8F5] font-semibold text-base hover:bg-[#2A2926] active:scale-98 transition-all shadow-md"
              >
                <span>Kontakt os for en uforpligtende aftale</span>
                <i className="fa-regular fa-arrow-right text-sm" aria-hidden="true" />
              </Link>
              <Link
                to="/saadan-arbejder-vi"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-[#D5CFC5] bg-white text-[#161514] font-medium text-sm hover:bg-[#F2ECE4] transition-colors"
              >
                <span>Se processen</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Banner */}
      <section className="py-5 bg-[#161514] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full bg-[#C5A880] text-[#161514] text-xs font-bold uppercase tracking-wider">
              Særtilbud
            </span>
            <p className="text-sm sm:text-base font-medium text-[#FAF8F5]">
              Betal for 2 måneder og få 1 måned helt gratis – altid 0 binding!
            </p>
          </div>
          <Link
            to="/kontakt"
            className="text-xs sm:text-sm font-semibold text-[#C5A880] hover:text-white underline underline-offset-4 transition-colors shrink-0"
          >
            Få en uforpligtende aftale i dag &rarr;
          </Link>
        </div>
      </section>

      {/* Ydelser overblik - Dark Modern Feature Grid */}
      <ServicesOverviewGrid />

      {/* Services List Section */}
      <section className="py-20 md:py-28 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {serviceCategories.map((service, index) => {
            const isReversed = index % 2 !== 0;
            return (
              <div
                key={service.id}
                id={service.slug}
                className="scroll-mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                {/* Visual Column */}
                <div
                  className={`lg:col-span-5 ${
                    isReversed ? 'lg:order-2' : 'lg:order-1'
                  }`}
                >
                  <div className="relative rounded-3xl overflow-hidden shadow-md aspect-4/3 bg-[#161514] border border-[#E5DFD6] group">
                    <img
                      src={getOptimizedImageUrl(service.image, {
                        width: 900,
                        quality: 85,
                        format: 'auto',
                      })}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 px-3.5 py-1.5 rounded-full bg-[#161514]/80 backdrop-blur-xs text-xs font-semibold text-[#FAF8F5] uppercase tracking-wider">
                      {service.tag}
                    </div>
                  </div>
                </div>

                {/* Content Column */}
                <div
                  className={`lg:col-span-7 space-y-6 ${
                    isReversed ? 'lg:order-1' : 'lg:order-2'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-[#A8885B]">
                      <i className={`${service.icon} text-2xl`} aria-hidden="true" />
                      <span className="text-xs uppercase tracking-widest font-bold">
                        {service.tag}
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#161514]">
                      {service.title}
                    </h2>
                    <p className="text-base text-[#5C5852] leading-relaxed">
                      {service.subtitle}
                    </p>
                  </div>

                  <p className="text-sm sm:text-base text-[#6E685F] leading-relaxed">
                    {service.description}
                  </p>

                  {/* Feature Checklist */}
                  <div className="pt-2">
                    <h4 className="text-xs uppercase tracking-wider font-bold text-[#8C867D] mb-4">
                      Servicen inkluderer bl.a.:
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.features.map((feature, fIdx) => (
                        <div key={fIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#423E38]">
                          <span className="shrink-0 w-5 h-5 rounded-full bg-[#EAE4DC] text-[#161514] flex items-center justify-center text-[10px] mt-0.5">
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="pt-4 flex flex-wrap items-center gap-4">
                    <Link
                      to={`/ydelser/${service.slug}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#161514] hover:text-[#A8885B] transition-colors"
                    >
                      <span>Læs mere om servicen</span>
                      <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                    <span className="text-gray-300">|</span>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#A8885B] hover:text-[#161514] transition-colors"
                    >
                      <span>Kontakt om denne ydelse</span>
                      <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-[#F5F1EB] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
              Hvorfor vælge Myserviceforyou?
            </h2>
            <p className="text-base text-[#5C5852]">
              En fleksibel, transparent og jordnær samarbejdspartner, der hjælper din forretning med at få succes på sociale medier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5DFD6] rounded-3xl p-8 space-y-4 shadow-xs"
              >
                <div className="text-[#C5A880] text-3xl">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-[#161514] tracking-tight">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#5C5852] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparent Agreement Info Box */}
      <section className="py-16 bg-white border-b border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="w-12 h-12 rounded-full bg-[#FAF8F5] border border-[#E5DFD6] text-[#A8885B] flex items-center justify-center mx-auto text-xl">
            <i className="fa-light fa-comments" aria-hidden="true" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-[#161514]">
            Hvorfor har vi ikke standardiserede prispakker?
          </h3>
          <p className="text-base text-[#5C5852] leading-relaxed">
            Fordi ingen virksomheder er ens. Et boutique-hotel eller en Airbnb-udlejer, der ønsker flere direkte værelsesbookinger og fyldte kalendere, har et helt andet behov end en restaurant med menuskift, en butik eller en håndværksvirksomhed. Vi tilpasser mængden af klipning, sparring og kalenderstyring til jeres unikke situation, så I kun betaler for det, der skaber reel værdi for jer. Altid med 0 binding.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#161514] text-[#FAF8F5] font-semibold text-sm hover:bg-[#2A2926] transition-colors"
            >
              <span>Kontakt os for en snak om jeres behov</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <CtaSection
        title="Skal vi hjælpe jeres forretning i gang?"
        subtitle="Husk vores aktuelle kampagne: Betal for 2 måneder og få 1 måned gratis – helt uden binding."
        primaryButtonText="Kontakt os uforpligtende"
        primaryButtonLink="/kontakt"
        backgroundImage="https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png"
      />
    </>
  );
}
